import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  administrationApi,
  type WorkspaceSnapshotRow,
  type GovernanceActivityEvent,
} from "@/features/administration/api/administration.api";
import { InviteMembersDialog } from "../components/InviteMembersDialog";
import { describeGovernanceActivity } from "@/features/administration/auditEventText";
import { useAdminWorkspacesModalStore } from "@/stores/adminWorkspacesModalStore";
import { WorkspaceHealthStatusText } from "@/features/administration/workspaceHealthDisplay";
import { useFeatureOn } from "@/features/feature-switches/featureSwitchSession";

function formatDate(value: string | null | undefined): string {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown time";
  return date.toLocaleString();
}

export default function AdministrationOverviewPage() {
  const [loading, setLoading] = useState(true);
  const [workspaceSnapshot, setWorkspaceSnapshot] = useState<WorkspaceSnapshotRow[]>([]);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [activity, setActivity] = useState<GovernanceActivityEvent[]>([]);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const policiesOn = useFeatureOn("governance-rule-engine");

  const loadOverview = async () => {
    setLoading(true);
    setSnapshotError(null);
    setActivityError(null);
    const results = await Promise.allSettled([
      administrationApi.getWorkspaceSnapshot({ page: 1, limit: 20 }),
      administrationApi.listRecentActivity({ limit: 20 }),
    ]);
    if (results[0].status === "fulfilled") {
      setWorkspaceSnapshot(results[0].value.data);
      setSnapshotError(null);
    } else {
      setWorkspaceSnapshot([]);
      setSnapshotError("Failed to load workspace snapshot.");
    }
    if (results[1].status === "fulfilled") {
      setActivity(results[1].value);
      setActivityError(null);
    } else {
      setActivity([]);
      setActivityError("Failed to load recent activity.");
    }
    setLoading(false);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      await loadOverview();
      if (!active) return;
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Administration Overview</h1>
        <p className="text-sm text-gray-600">
          Workspace snapshot, recent governance activity, and quick configuration access.
        </p>
      </header>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Workspace Snapshot</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">Workspace Name</th>
                <th className="px-4 py-3">Project Count</th>
                <th className="px-4 py-3">Budget Status</th>
                <th className="px-4 py-3">Capacity Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={4}>
                    Loading workspace snapshot...
                  </td>
                </tr>
              ) : snapshotError ? (
                <tr>
                  <td className="px-4 py-6 text-sm font-medium text-gray-900" colSpan={4}>
                    {snapshotError}
                  </td>
                </tr>
              ) : workspaceSnapshot.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={4}>
                    No workspaces available.
                  </td>
                </tr>
              ) : (
                workspaceSnapshot.map((workspace) => (
                  <tr key={workspace.workspaceId} className="border-t border-gray-200 text-sm text-gray-700">
                    <td className="px-4 py-3">{workspace.workspaceName}</td>
                    <td className="px-4 py-3">{workspace.projectCount}</td>
                    <td className="px-4 py-3">
                      <WorkspaceHealthStatusText status={workspace.budgetStatus} />
                    </td>
                    <td className="px-4 py-3">
                      <WorkspaceHealthStatusText status={workspace.capacityStatus} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white" data-testid="overview-governance-activity">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Recent Governance Activity</h2>
        </div>
        <div className="space-y-2 p-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading activity…</p>
          ) : activityError ? (
            <p className="text-sm font-medium text-gray-900" data-testid="overview-activity-error">
              {activityError}
            </p>
          ) : activity.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="overview-activity-empty">
              No recent governance activity.
            </p>
          ) : (
            activity.map((event) => (
              <div key={event.id} className="rounded border border-gray-200 p-3 text-sm">
                {/* ONE-TRUTH-FE-2: labelled from exceptionType/status via the one mapping; the
                    backend description carries raw codes and is not shown. */}
                <p className="font-medium text-gray-900">{describeGovernanceActivity(event)}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {formatDate(event.timestamp)} • {event.actorName || event.actorUserId || "System"}
                </p>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-gray-900">Quick Actions</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => useAdminWorkspacesModalStore.getState().open()}
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Browse workspaces
          </button>
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Invite People
          </button>
          {policiesOn ? (
          <Link
            to="/administration/governance"
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Open Governance Policies
          </Link>
          ) : null}
          <Link
            to="/administration/templates"
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Open Templates
          </Link>
          <Link
            to="/administration/audit-trail"
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            View Audit Trail
          </Link>
        </div>
      </section>

      <InviteMembersDialog isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}
